/**
 * Quick setup blocks 1–6 (plan 6.C). Markup mirrors the prototype (`eb-` classes).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import FeatureLock from "../../../../../components/FeatureLock/FeatureLock";
import { FEATURE_IDS } from "../../../../../constants/featureAccess";
import { notification } from "../../../../../utils/utility";
import ClrPicker from "../../../Components/Fields/ClrPicker";

import Icon from "../../ui/Icon";
import { FieldError, Helper, SelectWrap } from "../../ui/primitives";
import {
  COLOR_SWATCHES,
  CONFERENCING_VENUES,
  DURATION_MIN,
  DURATION_PRESETS,
  DURATION_STEP,
  E,
  FORMAT,
  GROUP_MIN_CAPACITY,
  INVITEE_CALL_STATUS,
  ROUTES,
  SCHEDULE_SOURCE,
  SECTIONS,
  STEPS,
  VENUE,
  VENUE_CARDS,
  VENUE_LOCATION_STATUS,
  VENUE_OTHER_DISPLAY_STATUS,
} from "../../state/constants";
import { companyTemplates, durationPresetFor } from "../../state/hydrate";
import { dayRangeLabel, hoursLabel, timezoneLabel } from "../../state/selectors";
import { rememberBuilderReturn } from "../../state/builderReturn";
import { isVenueConnected } from "../../state/validation";
import useQuickSetup from "./useQuickSetup";

const findIssue = (issues, field) => (issues || []).find((i) => i.field === field);
/** Inline messages appear once the user has edited the step (or on an existing event); the footer always counts them. */
const touched = (state, step) => state.save.version[step] > 0 || !state.meta.isNew;
const issueMessage = (state, step, issues, field) => {
  const i = findIssue(issues, field);
  return i && touched(state, step) ? i.message : null;
};

export const SetupBlock = ({ id, n, children, optional, paid, highlight }) => (
  <div className={`eb-setup-block ${optional ? "optional-block" : ""} ${paid ? "paid-setup-block" : ""} ${highlight ? "eb-highlight" : ""}`.trim()} id={id}>
    <div className={`eb-number-badge ${optional ? "color-number" : ""} ${paid ? "paid-number" : ""}`.trim()}>{n}</div>
    <div className="eb-setup-field">{children}</div>
  </div>
);

// ---- 1. Event name ---------------------------------------------------------------------------------------

export const EventNameBlock = ({ n = 1 }) => {
  const { state, setEventName, issues } = useQuickSetup();
  const serverError = state.save.fieldErrors[STEPS.TYPE].event_name;
  const error = serverError || issueMessage(state, STEPS.TYPE, issues, "event_name");
  return (
    <SetupBlock id="event-name-setting" n={n} highlight={state.ui.highlight === "event-name-setting"}>
      <label htmlFor="event-name">Event name</label>
      <p>Give the meeting a clear purpose. Duration is shown separately.</p>
      <input
        id="event-name"
        type="text"
        value={state.type.event_name}
        onChange={(e) => setEventName(e.target.value)}
        className={error ? "eb-has-error" : ""}
        maxLength={255}
        autoComplete="off"
      />
      <FieldError message={error} />
    </SetupBlock>
  );
};

// ---- 2. Booking format -------------------------------------------------------------------------------------

export const FormatBlock = ({ n = 2 }) => {
  const { state, setFormat, setCapacity, hasFeature, issues } = useQuickSetup();
  const isGroup = String(state.type.event_format_id) === FORMAT.GROUP;
  const groupAllowed = hasFeature(FEATURE_IDS.GROUP_EVENTS);
  const formatIssue = findIssue(issues, "event_format_id"); // group without feature 5 — always shown
  const capacityMessage = issueMessage(state, STEPS.TYPE, issues, "no_of_attendies");

  const groupButton = (
    <button type="button" className={isGroup ? "selected" : ""} onClick={() => groupAllowed && setFormat(FORMAT.GROUP)} disabled={!groupAllowed}>
      <span className="eb-format-icon group">
        <Icon name="users" size={17} />
      </span>
      <span>
        <strong>Group</strong>
        <small>One host and multiple invitees</small>
      </span>
      <i />
    </button>
  );

  return (
    <SetupBlock id="booking-format-setting" n={n} highlight={state.ui.highlight === "booking-format-setting"}>
      <label>Booking format</label>
      <p>Choose whether each time slot is for one invitee or a group.</p>
      <div className="eb-format-choice">
        <button type="button" className={!isGroup ? "selected" : ""} onClick={() => setFormat(FORMAT.ONE_TO_ONE)}>
          <span className="eb-format-icon">
            <Icon name="users" size={17} />
          </span>
          <span>
            <strong>One-to-one</strong>
            <small>One host and one invitee</small>
          </span>
          <i />
        </button>
        <FeatureLock featureId={FEATURE_IDS.GROUP_EVENTS}>{groupButton}</FeatureLock>
      </div>
      {isGroup ? (
        <label className="eb-capacity-field">
          Maximum invitees per time slot
          <input type="number" min={GROUP_MIN_CAPACITY} max="100" value={state.type.no_of_attendies} onChange={(e) => setCapacity(e.target.value)} />
        </label>
      ) : null}
      <FieldError message={(formatIssue && formatIssue.message) || capacityMessage} />
    </SetupBlock>
  );
};

// ---- 3. Duration ------------------------------------------------------------------------------------------------

export const DurationBlock = ({ n = 3 }) => {
  const { state, setDuration, goToSection, issues } = useQuickSetup();
  const inviteeChooses = state.availability.invitee_set_duration_status === E;
  const minutes = parseInt(state.availability.event_duration_in_minutes, 10) || 0;
  const preset = durationPresetFor(minutes);
  const [customOpen, setCustomOpen] = useState(preset === "custom");
  useEffect(() => {
    if (preset === "custom") setCustomOpen(true);
  }, [preset]);
  const showCustom = customOpen || preset === "custom";

  return (
    <SetupBlock id="duration-setting" n={n} highlight={state.ui.highlight === "duration-setting"}>
      <label>Duration</label>
      <p>Invitees can select a time that fits this length.</p>
      <div className="eb-choice-row">
        {DURATION_PRESETS.map((d) => (
          <button
            type="button"
            key={d}
            className={!inviteeChooses && preset === d && !customOpen ? "selected" : ""}
            onClick={() => {
              setCustomOpen(false);
              setDuration(d);
            }}
          >
            {d} min
          </button>
        ))}
        <button
          type="button"
          className={`custom-choice ${!inviteeChooses && showCustom ? "selected" : ""}`.trim()}
          onClick={() => {
            setCustomOpen(true);
            if (inviteeChooses) setDuration(minutes || DURATION_MIN);
          }}
        >
          Custom
        </button>
        {inviteeChooses ? (
          <button type="button" className="selected" disabled>
            Invitee chooses
          </button>
        ) : null}
      </div>
      {!inviteeChooses && showCustom ? (
        <label className="eb-capacity-field">
          Minutes
          <input
            type="number"
            min={DURATION_MIN}
            step={DURATION_STEP}
            value={minutes || ""}
            onChange={(e) => setDuration(e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0)}
          />
        </label>
      ) : null}
      {inviteeChooses ? (
        <Helper>
          Invitees pick their own duration.{" "}
          <button type="button" className="eb-link-btn" onClick={() => goToSection(SECTIONS.AVAILABILITY)}>
            Change in Availability &amp; rules
          </button>
        </Helper>
      ) : null}
      <FieldError message={issueMessage(state, STEPS.AVAILABILITY, issues, "event_duration_in_minutes")} />
    </SetupBlock>
  );
};

// ---- 4. Meeting location ---------------------------------------------------------------------------------------

const CONNECT_ROUTE = {
  [VENUE.GOOGLE_MEET]: ROUTES.MEET_INTEGRATION,
  [VENUE.ZOOM]: ROUTES.ZOOM_INTEGRATION,
  [VENUE.MICROSOFT_TEAMS]: ROUTES.TEAMS_INTEGRATION,
};

const VenueDetail = () => {
  const { state, setTypeField, issues } = useQuickSetup();
  const t = state.type;
  const venueId = parseInt(t.venue_id, 10);
  const err = (field) => issueMessage(state, STEPS.TYPE, issues, field);

  if (venueId === VENUE.PHONE) {
    return (
      <div className="eb-venue-detail">
        <div className="eb-radio-inline" role="radiogroup" aria-label="Who calls">
          <label>
            <input type="radio" name="invitee_call_status" checked={t.invitee_call_status === INVITEE_CALL_STATUS.ME} onChange={() => setTypeField({ invitee_call_status: INVITEE_CALL_STATUS.ME })} />
            I will call my invitee
          </label>
          <label>
            <input type="radio" name="invitee_call_status" checked={t.invitee_call_status === INVITEE_CALL_STATUS.INVITEE} onChange={() => setTypeField({ invitee_call_status: INVITEE_CALL_STATUS.INVITEE })} />
            Let my invitee call me
          </label>
        </div>
        {t.invitee_call_status === INVITEE_CALL_STATUS.INVITEE ? (
          <label>
            Your phone number
            <PhoneInput containerClass="custom_phn_inp" country="us" value={t.host_phone_no} onChange={(value) => setTypeField({ host_phone_no: value })} />
            <FieldError message={err("host_phone_no")} />
          </label>
        ) : null}
      </div>
    );
  }

  if (venueId === VENUE.IN_PERSON) {
    const legacy = t.venue_location_status === VENUE_LOCATION_STATUS.INVITEE_ADD_LOCATION;
    return (
      <div className="eb-venue-detail">
        <label>
          Address
          <input type="text" value={t.venue_location} onChange={(e) => setTypeField({ venue_location: e.target.value })} className={err("venue_location") ? "eb-has-error" : ""} />
          <FieldError message={err("venue_location")} />
        </label>
        <label>
          Location notes
          <textarea value={t.venue_location_notes} onChange={(e) => setTypeField({ venue_location_notes: e.target.value })} />
        </label>
        <label>
          Show the address
          <select value={t.venue_location_status} onChange={(e) => setTypeField({ venue_location_status: e.target.value })}>
            <option value={VENUE_LOCATION_STATUS.DISPLAY_BOOKING}>Display location while booking</option>
            <option value={VENUE_LOCATION_STATUS.DISPLAY_AFTER_CONFIRMED}>Display after booking is confirmed</option>
            {legacy ? <option value={VENUE_LOCATION_STATUS.INVITEE_ADD_LOCATION}>Invitee adds the location</option> : null}
          </select>
          <FieldError message={err("venue_location_status")} />
        </label>
      </div>
    );
  }

  if (venueId === VENUE.OTHER) {
    return (
      <div className="eb-venue-detail">
        <label>
          Notes
          <textarea value={t.venue_other_notes} onChange={(e) => setTypeField({ venue_other_notes: e.target.value })} className={err("venue_other_notes") ? "eb-has-error" : ""} />
          <FieldError message={err("venue_other_notes")} />
        </label>
        <label>
          Show notes
          <select value={t.venue_other_display_status} onChange={(e) => setTypeField({ venue_other_display_status: e.target.value })}>
            <option value={VENUE_OTHER_DISPLAY_STATUS.BOOKING_NOTES}>Display booking notes</option>
            <option value={VENUE_OTHER_DISPLAY_STATUS.AFTER_CONFIRMED}>Display after booking is confirmed</option>
          </select>
        </label>
      </div>
    );
  }
  return null;
};

export const LocationBlock = ({ n = 4 }) => {
  const { state, setVenue, issues } = useQuickSetup();
  const navigate = useNavigate();
  const { connectedVenues, connectedVenuesLoaded } = state.lookups;
  const selected = parseInt(state.type.venue_id, 10);
  const [connectAttemptId, setConnectAttemptId] = useState(null);
  const venueIssue = findIssue(issues, "venue_id");
  const connectIssue = venueIssue && venueIssue.message.indexOf("Connect this conferencing") === 0;
  // The connect warning stays hidden until the user clicks that venue. Other location errors still follow the step.
  const venueMessage = connectIssue
    ? connectAttemptId === selected
      ? venueIssue.message
      : null
    : issueMessage(state, STEPS.TYPE, issues, "venue_id");

  const chooseVenue = (card) => {
    const conferencing = CONFERENCING_VENUES.includes(card.id);
    const needsConnect = conferencing && connectedVenuesLoaded && !isVenueConnected(card.id, connectedVenues);
    if (!needsConnect) {
      setVenue(card.id);
      return;
    }
    setConnectAttemptId(card.id);
    notification.warning(
      `${card.title} isn't connected yet. Opening the Integrations page so you can connect it.`,
      "Connect this venue"
    );
    rememberBuilderReturn();
    navigate(CONNECT_ROUTE[card.id]);
  };

  // connected conferencing first, then the rest in the constant's order
  const cards = useMemo(() => {
    if (!connectedVenuesLoaded) return VENUE_CARDS;
    const rank = (card) => (CONFERENCING_VENUES.includes(card.id) && isVenueConnected(card.id, connectedVenues) ? 0 : 1);
    return [...VENUE_CARDS].sort((a, b) => rank(a) - rank(b)); // stable sort keeps the constant's order within a rank
  }, [connectedVenues, connectedVenuesLoaded]);

  return (
    <SetupBlock id="location-setting" n={n} highlight={state.ui.highlight === "location-setting"}>
      <label>Meeting location</label>
      <p>{state.meta.isNew ? "We selected your preferred conferencing option." : "Where this event takes place."}</p>
      <div className="eb-option-grid">
        {cards.map((card) => {
          const conferencing = CONFERENCING_VENUES.includes(card.id);
          const connected = conferencing && connectedVenuesLoaded && isVenueConnected(card.id, connectedVenues);
          const needsConnect = conferencing && connectedVenuesLoaded && !connected;
          const isSelected = selected === card.id;
          const sub = needsConnect ? "Not connected" : connected ? "Connected" : card.sub;
          const icon = <span className={`eb-option-icon ${card.iconClass || ""}`.trim()}>{card.icon ? <Icon name={card.icon} size={17} /> : card.glyph}</span>;
          return (
            <button
              type="button"
              key={card.id}
              className={`${isSelected ? "selected" : ""} ${connected ? "is-connected" : ""} ${needsConnect ? "needs-connect" : ""}`.trim()}
              onClick={() => chooseVenue(card)}
              aria-pressed={needsConnect ? undefined : isSelected}
              aria-label={needsConnect ? `${card.title}, not connected. Opens the Integrations page.` : undefined}
            >
              {icon}
              <span>
                <strong>{card.title}</strong>
                <small>{sub}</small>
              </span>
              {needsConnect ? <em className="eb-venue-connect">Connect</em> : <i />}
            </button>
          );
        })}
      </div>
      <VenueDetail />
      <FieldError message={venueMessage} />
    </SetupBlock>
  );
};

// ---- 5. Availability -------------------------------------------------------------------------------------------------

export const AvailabilityBlock = ({ n = 5 }) => {
  const { state, applyTemplate, switchToCustomHours, goToSection, issues } = useQuickSetup();
  const { scheduleTemplates, scheduleTemplatesLoaded, eventConfig } = state.lookups;
  const templates = companyTemplates(scheduleTemplates);
  const a = state.availability;
  const value = a.scheduleSource === SCHEDULE_SOURCE.CUSTOM ? "custom" : String(a.templateId || "");
  const [busy, setBusy] = useState(false);
  const issue = findIssue(issues, "schedules");

  const onChange = async (e) => {
    const v = e.target.value;
    if (v === "custom") {
      switchToCustomHours();
      goToSection(SECTIONS.AVAILABILITY);
      return;
    }
    setBusy(true);
    await applyTemplate(v);
    setBusy(false);
  };

  return (
    <SetupBlock id="availability-setting" n={n} highlight={state.ui.highlight === "availability-setting"}>
      <label>Availability</label>
      <p>Use a saved schedule or make changes later.</p>
      <SelectWrap icon="calendar">
        <select value={value} onChange={onChange} disabled={busy || !scheduleTemplatesLoaded} aria-label="Availability schedule">
          {value === "" ? <option value="">{scheduleTemplatesLoaded ? "Choose a schedule" : "Loading…"}</option> : null}
          {templates.map((t) => (
            <option key={t.value} value={String(t.value)}>
              {t.label}
            </option>
          ))}
          <option value="custom">Custom availability</option>
        </select>
      </SelectWrap>
      <div className="eb-availability-summary">
        <span>{dayRangeLabel(state)}</span>
        <strong>{hoursLabel(state, (eventConfig || {}).time_format) || "No hours set"}</strong>
        <em>{timezoneLabel(state) || "Time zone not set"}</em>
      </div>
      <FieldError message={issue ? issue.message : null} />
    </SetupBlock>
  );
};

// ---- 6. Event color ----------------------------------------------------------------------------------------------------

export const ColorBlock = ({ n = 6 }) => {
  const { state, setColor } = useQuickSetup();
  const color = String(state.details.event_color || "").toLowerCase();
  const isPreset = COLOR_SWATCHES.map((c) => c.toLowerCase()).includes(color);
  return (
    <SetupBlock id="event-color-setting" n={n} optional highlight={state.ui.highlight === "event-color-setting"}>
      <label>
        Event color <span className="eb-optional-label">Optional</span>
      </label>
      <p>Use color to recognize this event in your calendar and event list.</p>
      <div className="eb-color-picker" role="radiogroup" aria-label="Event color">
        {COLOR_SWATCHES.map((c) => (
          <button
            type="button"
            key={c}
            className={color === c.toLowerCase() ? "selected" : ""}
            aria-label={`Select color ${c}`}
            aria-checked={color === c.toLowerCase()}
            role="radio"
            style={{ backgroundColor: c }}
            onClick={() => setColor(c)}
          >
            <Icon name="check" size={14} />
          </button>
        ))}
        <span className={`eb-custom-color-pop ${!isPreset && color ? "selected" : ""}`.trim()} title="Custom color">
          <ClrPicker value={isPreset ? "" : state.details.event_color} onColorPickerChange={(c) => setColor(c)} />
        </span>
      </div>
    </SetupBlock>
  );
};
